import { useCurrentFacility } from '@/hooks/useCurrentFacility'
import { useMemo, useState } from 'react'
import { Building2, Loader2, MapPin, Search, Sparkles } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { usePermission } from '@/lib/permissions'
import { useCapacitySummary } from '@/hooks/useStorage'
import { useFacilityLocations, useSetEquipmentRoom } from '@/hooks/useFacility'
import { RoomPicker } from '@/components/equipment/facility/RoomPicker'
import { FacilityStudio } from '@/components/equipment/facility/FacilityStudio'
import { BuildingElevation, RoomTile } from '@/components/equipment/facility/BuildingElevation'
import { FacilityExplorer } from '@/components/equipment/facility/FacilityExplorer'
import { FacilitySwitcher } from '@/components/equipment/facility/FacilitySwitcher'
import { PLACE_SEPARATOR, filterTree, formatPlace, indexRooms, locationStats } from '@/lib/facilityLocation'
import type { LocationTree } from '@/api/facilityApi'
import type { CapacitySummaryRow } from '@/types/storage'

/**
 * Where storage stands (z8nrz7fw25): Facility → Building → Floor → Room, and
 * the equipment in each room. The other half of a location — the inside of a
 * unit, Zone → Rack → Box — opens from a unit here, so the two read as one
 * address.
 *
 * Buildings down the side, each a miniature stack of its floors; the chosen one
 * drawn as an elevation (`BuildingElevation`, the same drawing the facility
 * set-up builds on), with every unit showing how full it is.
 */
export function StorageLocationsView() {
  const { can } = usePermission()
  const locations = useFacilityLocations(can('equipment.read') || can('inventory.view'))
  const capacity = useCapacitySummary()
  // One facility choice for the whole app (z8nrz7fw25): switching it here switches it everywhere.
  const [facilityId, setFacilityId] = useCurrentFacility()
  const [query, setQuery] = useState('')
  const [setupOpen, setSetupOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canPlace = can('equipment.manage')
  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That change was not saved.')
    }
  }

  const byEquipment = useMemo(
    () => new Map((capacity.data ?? []).filter((r) => r.equipmentId).map((r) => [r.equipmentId as string, r])),
    [capacity.data],
  )

  if (locations.isLoading) {
    return (
      <div className='flex items-center gap-2 py-16 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' /> Loading locations…
      </div>
    )
  }
  if (locations.isError || !locations.data) {
    return (
      <p className='py-16 text-center text-sm text-muted-foreground'>
        Locations could not be loaded. Check that the Equipment module is on for this tenant.
      </p>
    )
  }

  const tree = locations.data
  const facility = tree.facilities.find((f) => f.id === facilityId) ?? tree.facilities[0] ?? null
  // Counts and search cover the chosen facility only; rooms on no floor and
  // units in no room belong to none, so they stay in view.
  const scoped = facility ? { ...tree, facilities: [facility] } : tree
  const searching = query.trim() !== ''
  const shown = filterTree(scoped, query)
  const stats = locationStats(scoped)

  return (
    <div className='space-y-5'>
      <div className='flex flex-wrap items-center gap-3'>
        <div className='relative w-full max-w-sm'>
          <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Find a unit, room or building'
            className='pl-8'
            aria-label='Find a location'
          />
        </div>
        <p className='text-sm text-muted-foreground'>
          {stats.placed} {stats.placed === 1 ? 'unit' : 'units'} in {stats.rooms} {stats.rooms === 1 ? 'room' : 'rooms'}
          {stats.unplaced > 0 && <span className='text-amber-700'>, {stats.unplaced} not placed yet</span>}
        </p>
        {canPlace && (
          <Button variant='outline' size='sm' className='ml-auto gap-1.5' onClick={() => setSetupOpen(true)}>
            <Building2 className='h-4 w-4' /> Set up facility
          </Button>
        )}
      </div>
      <FacilityStudio open={setupOpen} onOpenChange={setSetupOpen} />

      {tree.facilities.length === 0 && tree.unmappedRooms.length === 0 ? (
        <EmptySite onSetUp={canPlace ? () => setSetupOpen(true) : undefined} />
      ) : searching ? (
        <SearchResults tree={shown} byEquipment={byEquipment} />
      ) : (
        facility && (
          <div className='space-y-3'>
            {/* Always shown, even with one facility: it is where another is added. */}
            <FacilitySwitcher
              facilities={tree.facilities}
              selected={facility}
              onSelect={setFacilityId}
              editable={canPlace}
              run={run}
            />

            {/* The same explorer as the facility set-up: buildings and floors,
                the floor in 3D, its rooms, and the chosen room. */}
            <div className='flex h-[78vh] min-h-[560px] overflow-hidden rounded-2xl border bg-white'>
              <FacilityExplorer
                key={facility.id}
                tree={tree}
                facility={facility}
                byEquipment={byEquipment}
                editable={canPlace}
                run={run}
              />
            </div>
            {error && (
              <p role='alert' className='text-xs text-destructive'>
                {error}
              </p>
            )}
          </div>
        )
      )}

      {shown.unplaced.length > 0 && <Unplaced units={shown.unplaced} tree={tree} canPlace={canPlace} />}
    </div>
  )
}

/** A search shows every building that has a match, each with its full address. */
function SearchResults({ tree, byEquipment }: { tree: LocationTree; byEquipment: Map<string, CapacitySummaryRow> }) {
  const sections = tree.facilities.flatMap((f) =>
    f.buildings.map((b) => ({ key: b.id, title: `${f.code}${PLACE_SEPARATOR}${b.name}`, facility: `${f.code} · ${f.name}`, b })),
  )
  if (sections.length === 0 && tree.unmappedRooms.length === 0 && tree.unplaced.length === 0) {
    return <p className='py-10 text-center text-sm text-muted-foreground'>Nothing here matches. Try a unit ID or a room number.</p>
  }
  return (
    <div className='space-y-6'>
      {sections.map((s) => (
        <section key={s.key} className='space-y-2'>
          <h3 className='flex items-center gap-1.5 text-sm font-semibold text-slate-900'>
            <MapPin className='h-3.5 w-3.5 text-primary' /> {s.title}
          </h3>
          <div className='rounded-2xl bg-slate-50/80 p-4'>
            <BuildingElevation building={s.b} facilityLabel={s.facility} byEquipment={byEquipment} />
          </div>
        </section>
      ))}
      {tree.unmappedRooms.length > 0 && (
        <div className='flex flex-wrap gap-3'>
          {tree.unmappedRooms.map((r) => (
            <RoomTile key={r.id} room={r} byEquipment={byEquipment} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Equipment in no room. Its old free-text location is shown, and when that
 * names exactly one room the room is offered — a suggestion a person confirms,
 * never a link made on its own.
 */
function Unplaced({ units, tree, canPlace }: { units: LocationTree['unplaced']; tree: LocationTree; canPlace: boolean }) {
  const setRoom = useSetEquipmentRoom()
  const [picking, setPicking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const rooms = useMemo(() => indexRooms(tree), [tree])

  const place = (equipmentId: string, roomId: string | null) => {
    if (!roomId) return
    setError(null)
    setRoom.mutate({ equipmentId, roomId }, { onSuccess: () => setPicking(null), onError: (e) => setError(e.message) })
  }

  return (
    <section className='space-y-2 rounded-2xl border border-amber-200 bg-amber-50/40 p-4'>
      <h3 className='text-sm font-semibold text-slate-900'>Not in a room yet ({units.length})</h3>
      <p className='text-xs text-muted-foreground'>
        These have no address yet. Place each in its room and it reads as Facility › Building › Floor › Room everywhere.
      </p>
      {error && (
        <p role='alert' className='text-xs text-destructive'>
          {error}
        </p>
      )}
      <ul className='divide-y rounded-xl border bg-white'>
        {units.map((u) => {
          const suggested = u.suggestedRoomId ? rooms.get(u.suggestedRoomId) : undefined
          return (
            <li key={u.id} className='flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2'>
              <span className='min-w-0 flex-1'>
                <span className='block text-sm font-medium text-slate-900'>{u.label}</span>
                <span className='block text-[11px] text-muted-foreground'>
                  {u.typeName}
                  {u.hint && <> · noted as “{u.hint}”</>}
                </span>
              </span>
              {canPlace &&
                (picking === u.id ? (
                  <div className='w-full sm:w-72'>
                    <RoomPicker value={null} onChange={(roomId) => place(u.id, roomId)} disabled={setRoom.isPending} />
                  </div>
                ) : (
                  <div className='flex flex-wrap gap-1.5'>
                    {suggested && u.suggestedRoomId && (
                      <Button
                        size='sm'
                        variant='outline'
                        className='gap-1'
                        disabled={setRoom.isPending}
                        onClick={() => place(u.id, u.suggestedRoomId)}
                      >
                        <Sparkles className='h-3.5 w-3.5' /> Place in {formatPlace(suggested)}
                      </Button>
                    )}
                    <Button size='sm' variant='ghost' onClick={() => setPicking(u.id)}>
                      Choose room
                    </Button>
                  </div>
                ))}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function EmptySite({ onSetUp }: { onSetUp?: () => void }) {
  return (
    <div className='rounded-2xl border border-dashed px-6 py-14 text-center'>
      <Building2 className='mx-auto h-8 w-8 text-slate-300' />
      <p className='mt-2 text-sm font-medium text-slate-900'>No facility is set up yet</p>
      <p className='mt-1 text-sm text-muted-foreground'>
        Set up your facility once, with its buildings, floors and rooms. Then place each unit in its room.
      </p>
      {onSetUp && (
        <Button size='sm' className='mt-4 gap-1.5' onClick={onSetUp}>
          <Building2 className='h-4 w-4' /> Set up facility
        </Button>
      )}
    </div>
  )
}
